import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import ProtectedRoute, { PublicOnlyRoute } from "@/components/ProtectedRoute";
import RequireCompletedProfile from "@/components/RequireCompletedProfile";
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import AccountRecovery from "@/pages/auth/AccountRecovery";
import AppShell from "@/components/AppShell";
import ComingSoon from "@/pages/app/ComingSoon";
import MyProfile from "@/pages/app/MyProfile";
import EditProfile from "@/pages/app/EditProfile";
import ProfilePreview from "@/pages/app/ProfilePreview";
import Settings from "@/pages/app/Settings";
import Invitations from "@/pages/app/Invitations";
import BlockedUsers from "@/pages/app/BlockedUsers";
import DeleteAccount from "@/pages/app/DeleteAccount";
import PrivacySettings from "@/pages/app/PrivacySettings";
import EmailNotifications from "@/pages/app/EmailNotifications";
import Onboarding from "@/pages/app/Onboarding";
import Discover from "@/pages/app/Discover";
import DiscoverDetail from "@/pages/app/DiscoverDetail";
import MessagesInbox from "@/pages/app/MessagesInbox";
import ChatRoom from "@/pages/app/ChatRoom";
import Matches from "@/pages/app/Matches";
import MatchDetail from "@/pages/app/MatchDetail";
import Likes from "@/pages/app/Likes";
import Notifications from "@/pages/app/Notifications";
import Membership from "@/pages/app/Membership";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import AdminShell from "@/components/AdminShell";
import AdminModeration from "@/pages/admin/AdminModeration";
import AdminReportDetail from "@/pages/admin/AdminReportDetail";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminStatistics from "@/pages/admin/AdminStatistics";
import CookieBanner from "@/components/CookieBanner";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { Toaster } from "@/components/ui/sonner";

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <ProfileProvider>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route
                path="/login"
                element={
                  <PublicOnlyRoute>
                    <Login />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicOnlyRoute>
                    <Register />
                  </PublicOnlyRoute>
                }
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/account/recovery" element={<AccountRecovery />} />

              {/* Legal */}
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms" element={<TermsOfService />} />
              <Route path="/legal/cookies" element={<CookiePolicy />} />

              {/* Payment return URLs (outside app shell) */}
              <Route
                path="/payment/success"
                element={
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/cancel"
                element={
                  <ProtectedRoute>
                    <PaymentCancel />
                  </ProtectedRoute>
                }
              />

              {/* Admin shell (server re-verifies role on every /api/admin/*) */}
              <Route path="/admin" element={<AdminShell />}>
                <Route index element={<Navigate to="moderation" replace />} />
                <Route path="moderation" element={<AdminModeration />} />
                <Route path="moderation/:reportId" element={<AdminReportDetail />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
                <Route path="statistics" element={<AdminStatistics />} />
                <Route path="billing-config" element={<AdminBilling />} />
              </Route>

              {/* Authenticated app shell */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="discover" replace />} />
                <Route path="onboarding" element={<Onboarding />} />
                <Route
                  path="discover"
                  element={
                    <RequireCompletedProfile>
                      <Discover />
                    </RequireCompletedProfile>
                  }
                />
                <Route
                  path="discover/:userId"
                  element={
                    <RequireCompletedProfile>
                      <DiscoverDetail />
                    </RequireCompletedProfile>
                  }
                />
                <Route
                  path="matches"
                  element={
                    <RequireCompletedProfile>
                      <Matches />
                    </RequireCompletedProfile>
                  }
                />
                <Route
                  path="matches/:matchId"
                  element={
                    <RequireCompletedProfile>
                      <MatchDetail />
                    </RequireCompletedProfile>
                  }
                />
                <Route
                  path="likes"
                  element={
                    <RequireCompletedProfile>
                      <Likes />
                    </RequireCompletedProfile>
                  }
                />
                <Route
                  path="messages"
                  element={
                    <RequireCompletedProfile>
                      <MessagesInbox />
                    </RequireCompletedProfile>
                  }
                />
                <Route
                  path="messages/:matchId"
                  element={
                    <RequireCompletedProfile>
                      <ChatRoom />
                    </RequireCompletedProfile>
                  }
                />
                <Route path="profile" element={<MyProfile />} />
                <Route path="profile/edit" element={<EditProfile />} />
                <Route path="profile/preview" element={<ProfilePreview />} />
                <Route path="notifications" element={
                  <RequireCompletedProfile>
                    <Notifications />
                  </RequireCompletedProfile>
                } />
                <Route path="settings" element={<Settings />} />
                <Route path="settings/invitations" element={<Invitations />} />
                <Route path="settings/blocked-users" element={<BlockedUsers />} />
                <Route path="settings/privacy" element={<PrivacySettings />} />
                <Route path="settings/notifications" element={<EmailNotifications />} />
                <Route path="settings/delete-account" element={<DeleteAccount />} />
                <Route path="membership" element={<Membership />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster />
            <CookieBanner />
          </ProfileProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
